import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import { getMaintenanceConfig } from '../utils/admin/maintenanceService';
import { logger } from '../utils/logger';

/**
 * MaintenanceBlockedPage
 *
 * Página exibida quando uma rota está em manutenção.
 * Mostra mensagem personalizada e tempo estimado de volta.
 *
 * @component
 * @example
 * <MaintenanceBlockedPage route="/chat-interface" />
 */
const MaintenanceBlockedPage = ({ route = '/' }) => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    const loadMaintenanceInfo = async () => {
      try {
        const maintenanceConfig = await getMaintenanceConfig();
        setConfig(maintenanceConfig);
        logger.log('🔧 [Maintenance] Config carregada:', maintenanceConfig);
      } catch (error) {
        logger.error('❌ [Maintenance] Erro ao carregar config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMaintenanceInfo();
  }, []);

  // Countdown timer se houver estimatedEndTime
  useEffect(() => {
    if (!config?.estimatedEndTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const end = new Date(config.estimatedEndTime);
      const diff = end - now;

      if (diff <= 0) {
        setCountdown('Em breve');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m`);
      } else {
        setCountdown(`${minutes} minutos`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Atualiza a cada minuto

    return () => clearInterval(interval);
  }, [config?.estimatedEndTime]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleTryAgain = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground font-body">Verificando status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <div className="bg-card rounded-2xl shadow-xl p-8 md:p-12 text-center">
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 bg-warning/10 rounded-full mb-6"
          >
            <Icon
              name="Wrench"
              size={48}
              className="text-warning"
              aria-hidden="true"
            />
          </motion.div>

          {/* Title */}
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            {config?.globalMaintenance ? 'Sistema em Manutenção' : 'Funcionalidade em Manutenção'}
          </h1>

          {/* Message */}
          <div className="space-y-4 mb-8">
            <p className="font-body text-lg text-muted-foreground">
              {config?.maintenanceMessage ||
                'Estamos realizando melhorias no sistema. Em breve estaremos de volta.'}
            </p>

            {/* Countdown */}
            {countdown && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full"
              >
                <Icon name="Clock" size={16} />
                <span className="font-body-medium text-sm">
                  Retorno estimado: {countdown}
                </span>
              </motion.div>
            )}
          </div>

          {/* Route Info (dev only) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-6 p-3 bg-muted/50 rounded-lg">
              <p className="font-mono text-xs text-muted-foreground">
                Rota em manutenção: <span className="text-foreground font-semibold">{route}</span>
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              size="lg"
              iconName="Home"
              onClick={handleGoHome}
              className="font-body-medium"
            >
              Voltar ao Início
            </Button>
            <Button
              variant="default"
              size="lg"
              iconName="RefreshCw"
              onClick={handleTryAgain}
              className="font-body-medium"
            >
              Tentar Novamente
            </Button>
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 pt-6 border-t border-border"
          >
            <p className="font-body text-sm text-muted-foreground">
              Em caso de urgência, entre em contato com o suporte
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default MaintenanceBlockedPage;
